// Influencer collaborations — small table, fetched whole and filtered/aggregated
// client-side. Drives the "influencer collabs" KPI and the collaborations list.
import { supabase } from "@/lib/supabase";
import type { Collaboration, RegionFilter, BrandFilter } from "@/lib/api/types";

// Collaborations are a roster scoped by region/brand. The "active collabs" KPI
// is a point-in-time count (status active/published), intentionally independent
// of the date window — it answers "how many collabs are live now", not "this month".
export interface CollabQuery {
  region: RegionFilter;
  brand: BrandFilter;
}

export async function getCollaborations(q: CollabQuery): Promise<Collaboration[]> {
  let query = supabase
    .from("collaborations")
    .select("id, brand_id, region, platform_id, creator_handle, creator_name, status, reach, engagements, content_pieces, fee, started_at, published_at")
    .order("started_at", { ascending: false });
  if (q.region !== "all") query = query.eq("region", q.region);
  if (q.brand !== "all") query = query.eq("brand_id", q.brand);
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as Collaboration[];
}

export interface CollabStats {
  total: number;
  active: number;     // open: everything not completed (cancelled is excluded upstream)
  completed: number;
  proposed: number;
  total_reach: number;
  total_fee: number;
  avg_engagement_rate: number | null;
}

export function collabStats(collabs: Collaboration[]): CollabStats {
  // PostgREST decodes bigint/numeric as strings — Number() avoids "0" + "123" = "0123".
  const reach = collabs.reduce((s, c) => s + Number(c.reach), 0);
  const eng = collabs.reduce((s, c) => s + Number(c.engagements), 0);
  return {
    total: collabs.length,
    // "Active" means what the managing app itself calls Actief: every open
    // collaboration, from proposal to awaiting performance. The view already
    // leaves out cancelled ones, so only completed ones are not open. Counting
    // only the contract stages here made Pulse show 5 where the managing app,
    // which owns these statuses, shows 9.
    active: collabs.filter((c) => c.status !== "completed").length,
    completed: collabs.filter((c) => c.status === "completed").length,
    proposed: collabs.filter((c) => c.status === "proposed").length,
    total_reach: reach,
    total_fee: collabs.reduce((s, c) => s + Number(c.fee), 0),
    avg_engagement_rate: reach > 0 ? eng / reach : null,
  };
}
